import type { Language } from '@eformulieren/engine'
import type { ReactNode } from 'react'
import type { LoginOption } from './LoginChoice.js'
import { BusyLink } from './BusyButton.js'
import { type Bilingual, localeOf } from '../language/text.js'

/**
 * De melding bij een sessie die verloopt terwijl het formulier openstaat (R-154, R-165, ontwerp 6.4,
 * R-139, R-144).
 *
 * Ontwerp 6.4 zegt bij "Het token verloopt": het opslaan stopt met een melding en dan volgt opnieuw
 * inloggen, in plaats van stil te falen. Dit is die melding, met de knop. Het formulier blijft eronder
 * staan, zodat wat nog niet is opgeslagen er staat om over te nemen; dezelfde regel als bij een
 * verlaagd recht in diezelfde tabel.
 *
 * **Alleen zeggen wat waar is over de invoer.** De pagina weet per sectie of er een eigen wijziging
 * is die de pod niet heeft bevestigd (`createUnsavedTracker` in `apps/runtime/pagina/session.ts`).
 * Is er geen, dan is alles opgeslagen en zegt de melding dat; is er wel een, dan zegt ze dat de
 * laatste wijzigingen mogelijk niet zijn opgeslagen. "Mogelijk", want een wijziging die onderweg was
 * kan zijn aangekomen zonder dat de bevestiging terugkwam. Het formulier staat daarna op alleen lezen,
 * met de tekst nog te selecteren en te kopiëren, en dat zegt de melding ook.
 *
 * **Een link en geen knop met een klikhandler**, om dezelfde reden als bij het eerste inloggen
 * (`LoginChoice.tsx`): het is een navigatie naar een andere dienst. Eén ingang heet "Opnieuw
 * inloggen"; zijn het er meer, dan de kant erbij, en de naam van de ingang als de kant dubbel
 * voorkomt. Noemt de pod zijn ingangen niet, dan gaat de link naar de pagina zelf, die bij het
 * opkomen geen sessie vindt en de gewone keuze toont.
 */

/**
 * Wat de melding over de invoer zegt.
 *
 * `zonder-formulier` is voor een sessie die verliep voordat er een formulier in beeld stond: bij het
 * opkomen, of bij een sprong in de reeks, die eerst alles wegschrijft (R-122).
 */
export type SessionExpiredState = 'opgeslagen' | 'mogelijk-niet-opgeslagen' | 'zonder-formulier'

/**
 * Per stand twee delen: wat er met de invoer is (`status`), en hoe het verder gaat (`uitleg`). Op een
 * smal scherm valt de uitleg weg (`schil.css`, `.ef-sessie__uitleg`), zodat de melding het formulier
 * niet verdringt; wat er met de invoer is blijft altijd staan, en de knop zegt zelf hoe het verder gaat.
 */
const VERDER: Bilingual = {
  nl: 'Het formulier staat nu op alleen lezen. Log opnieuw in om verder te gaan; daarna komt u terug op dit formulier.',
  en: 'The form is now read-only. Sign in again to continue; afterwards you return to this form.',
}

const T = {
  titel: { nl: 'Uw sessie is verlopen.', en: 'Your session has expired.' },
  status: {
    opgeslagen: { nl: 'Wat u hebt ingevuld, is opgeslagen.', en: 'What you have entered has been saved.' },
    'mogelijk-niet-opgeslagen': {
      nl: 'Uw laatste wijzigingen zijn mogelijk niet opgeslagen; kopieer wat u wilt bewaren.',
      en: 'Your latest changes may not have been saved; copy anything you want to keep.',
    },
    'zonder-formulier': { nl: '', en: '' },
  },
  uitleg: {
    opgeslagen: VERDER,
    'mogelijk-niet-opgeslagen': VERDER,
    'zonder-formulier': {
      nl: 'Log opnieuw in om verder te gaan; daarna komt u terug op dit formulier.',
      en: 'Sign in again to continue; afterwards you return to this form.',
    },
  },
  knop: { nl: 'Opnieuw inloggen', en: 'Sign in again' },
  intern: { nl: 'Opnieuw inloggen als medewerker van NWO', en: 'Sign in again as an NWO employee' },
  extern: { nl: 'Opnieuw inloggen als aanvrager', en: 'Sign in again as an applicant' },
  groep: { nl: 'Opnieuw inloggen', en: 'Sign in again' },
} as const

function labelOf(option: LoginOption, options: readonly LoginOption[], language: Language): string {
  if (options.length === 1) return T.knop[language]
  const base = T[option.kant][language]
  const double = options.filter((other) => other.kant === option.kant).length > 1
  return double ? `${base} (${option.gastheer})` : base
}

export interface SessionExpiredProps {
  readonly taal: Language
  readonly stand: SessionExpiredState
  /** De ingangen aan de eigen kant, met het adres naar de inlogdienst; de pagina rekent ze uit. */
  readonly opties: readonly LoginOption[]
  /** Waar de link heen gaat als de pod zijn ingangen niet noemde: de pagina zelf. */
  readonly terugval: string
}

export function SessionExpired({ taal: language, stand: state, opties: options, terugval: fallback }: SessionExpiredProps): ReactNode {
  return (
    <div className="ef-sessie" lang={language}>
      <div className="ef-sessie__inhoud">
        {/* Alleen de tekst in de alert, zodat een wissel van stand opnieuw wordt voorgelezen en de
            links niet bij elke wissel mee. */}
        <p className="ef-sessie__tekst" role="alert">
          <strong className="ef-sessie__titel">{T.titel[language]}</strong>
          {T.status[state][language] === '' ? null : <> {T.status[state][language]}</>}
          <span className="ef-sessie__uitleg"> {T.uitleg[state][language]}</span>
        </p>
        <div className="ef-sessie__acties" role="group" aria-label={T.groep[language]}>
          {options.length === 0 ? (
            <BusyLink className="ef-actieknop ef-inlogkeuze__knop" data-soort="primair" href={fallback} busyText={BUSY_LOGIN[language]}>
              {T.knop[language]}
            </BusyLink>
          ) : (
            options.map((option) => (
              <BusyLink key={option.gastheer} className="ef-actieknop ef-inlogkeuze__knop" data-soort="primair" href={option.adres} busyText={BUSY_LOGIN[language]}>
                {labelOf(option, options, language)}
              </BusyLink>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

/** Wat een inlogknop zegt terwijl de browser naar de inlogdienst gaat (`BusyLink`). */
const BUSY_LOGIN = { nl: 'Bezig met doorsturen…', en: 'Redirecting…' } as const

/**
 * De waarschuwing vóór het verlopen van de sessie (WCAG 2.2.1, R-139, R-165; besluiten van 30 en 31
 * oktober 2026).
 *
 * Een paar minuten voor de vervaltijd van het token, met de tijd erin en een weg om verder te
 * kunnen: "Sessie verlengen". Dat gaat naar `<inlogdienst>/<gastheer>/verlengen`, die altijd langs de
 * identiteitsprovider gaat (`apps/inlog/src/server.ts`): met een sessie daar is het één omleiding
 * heen en terug zonder scherm, en de pagina komt terug op dezelfde instantie en dezelfde plek. Het is
 * een navigatie, dus de pagina laadt opnieuw; daarom verlengt de knop pas als alles is opgeslagen,
 * en zegt hij zolang dat hij opslaat (`bezig`). Wat er dan in beeld staat komt na het terugkomen uit
 * de opslag. "Later" sluit de waarschuwing; verloopt de sessie daarna toch, dan volgt de melding
 * hierboven.
 *
 * **Een knop en geen link**, anders dan bij opnieuw inloggen: de pagina moet eerst nagaan dat er
 * niets onbevestigd is (`onVerlengen`).
 *
 * **Zonder adres om te verlengen geen schijnknop.** De pagina haalt het adres uit de eigen ingang van
 * `/whoami` of uit `/ingangen`, en blijft dat laatste begrensd opnieuw proberen. Heeft ze geen van
 * beide, dan zegt de waarschuwing dat verlengen nu niet lukt en wat er bij het verlopen gebeurt. Tot 3
 * november 2026 stond er dan een link "Opnieuw inloggen" naar de pagina zelf, maar met een sessie die
 * nog geldt laadt die alleen het formulier opnieuw, met dezelfde vervaltijd: een knop die niets
 * verlengt. Komt het adres later alsnog, dan staat de knop er vanaf dat moment.
 *
 * `role="alert"` omdat hij vanzelf verschijnt terwijl de invuller iets anders doet, en een
 * schermlezer hem anders niet hoort; de focus verspringt niet.
 */
export interface SessionWarningProps {
  readonly taal: Language
  /** Wanneer de sessie verloopt, in milliseconden sinds 1970. */
  readonly verlooptOp: number
  /** De ingang waarlangs wordt verlengd, met het adres van `verlengen`; de pagina rekent het uit. */
  readonly opties: readonly LoginOption[]
  readonly onLater: () => void
  /** Verlengen langs deze ingang; de pagina wacht tot alles is opgeslagen en navigeert dan. */
  readonly onVerlengen: (adres: string) => void
  /** Of de pagina nog wacht tot alles is opgeslagen, of al doorstuurt. */
  readonly bezig?: 'opslaan' | 'doorsturen'
  /** Lukte het opslaan niet op tijd, dan zegt de pagina dat hier, en blijft de knop staan. */
  readonly melding?: string
}

export function SessionWarning({
  taal: language,
  verlooptOp: expiresAt,
  opties: options,
  onLater,
  onVerlengen: onRenew,
  bezig: busy,
  melding: notice,
}: SessionWarningProps): ReactNode {
  const time = new Date(expiresAt).toLocaleTimeString(localeOf(language), { hour: '2-digit', minute: '2-digit' })
  return (
    <div className="ef-sessie" lang={language} data-stand="waarschuwing">
      <div className="ef-sessie__inhoud">
        <p className="ef-sessie__tekst" role="alert">
          <strong className="ef-sessie__titel">{WARNING.titel[language].replace('{tijd}', time)}</strong>{' '}
          <span className="ef-sessie__uitleg">{(options.length === 0 ? WARNING.uitlegZonderVerlengen : WARNING.uitleg)[language]}</span>
          {notice === undefined ? null : <> {notice}</>}
        </p>
        <div className="ef-sessie__acties" role="group" aria-label={WARNING.groep[language]}>
          <button type="button" className="ef-actieknop" data-soort="secundair" onClick={onLater} disabled={busy !== undefined}>
            {WARNING.later[language]}
          </button>
          {options.map((option) => (
            <button
              key={option.gastheer}
              type="button"
              className="ef-actieknop ef-inlogkeuze__knop"
              data-soort="primair"
              disabled={busy !== undefined}
              aria-busy={busy !== undefined}
              onClick={() => onRenew(option.adres)}
            >
              {busy === 'opslaan' ? WARNING.opslaan[language] : busy === 'doorsturen' ? BUSY_LOGIN[language] : renewLabelOf(option, options, language)}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

function renewLabelOf(option: LoginOption, options: readonly LoginOption[], language: Language): string {
  if (options.length === 1) return WARNING.knop[language]
  const base = WARNING[option.kant][language]
  const double = options.filter((other) => other.kant === option.kant).length > 1
  return double ? `${base} (${option.gastheer})` : base
}

const WARNING = {
  titel: { nl: 'Uw sessie verloopt om {tijd}.', en: 'Your session expires at {tijd}.' },
  uitleg: {
    nl: 'Wat u invult wordt opgeslagen. Verleng uw sessie om zonder onderbreking verder te werken; daarna komt u terug op deze plek in het formulier.',
    en: 'What you enter is saved. Extend your session to keep working without interruption; afterwards you return to this place in the form.',
  },
  uitlegZonderVerlengen: {
    nl: 'Wat u invult wordt opgeslagen. Uw sessie verlengen lukt op dit moment niet; na het verlopen logt u opnieuw in en komt u terug op dit formulier.',
    en: 'What you enter is saved. Extending your session is not possible right now; after it expires you sign in again and return to this form.',
  },
  groep: { nl: 'Sessie verlengen', en: 'Extend session' },
  knop: { nl: 'Sessie verlengen', en: 'Extend session' },
  intern: { nl: 'Sessie verlengen als medewerker van NWO', en: 'Extend session as an NWO employee' },
  extern: { nl: 'Sessie verlengen als aanvrager', en: 'Extend session as an applicant' },
  opslaan: { nl: 'Bezig met opslaan…', en: 'Saving…' },
  later: { nl: 'Later', en: 'Later' },
} as const
